import dayjs from 'dayjs';
import { titleCase } from 'string-ts';

export const getBlueShade = (share: number, maxShare: number) => {
  const minColor = [224, 242, 254] as const;
  const maxColor = [7, 89, 133] as const;

  const minVisibilityThreshold = 0.05;

  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  let t = share / maxShare;
  t = minVisibilityThreshold + (1 - minVisibilityThreshold) * t;
  t = clamp(t, minVisibilityThreshold, 1);

  const r = Math.round(minColor[0] + (maxColor[0] - minColor[0]) * t);
  const g = Math.round(minColor[1] + (maxColor[1] - minColor[1]) * t);
  const b = Math.round(minColor[2] + (maxColor[2] - minColor[2]) * t);

  return `rgb(${r}, ${g}, ${b})`;
};

export const calculateTrend = (data: Array<{ label: string; value: number }>) => {
  if (data.length < 2) {
    return { direction: 'No trend data', percentage: 0 };
  }

  const lastMonthValue = data[data.length - 1]?.value ?? 0;
  const previousMonthValue = data[data.length - 2]?.value ?? 0;

  if (previousMonthValue === 0) {
    return { direction: 'No trend data', percentage: 0 };
  }

  const percentageChange = ((lastMonthValue - previousMonthValue) / previousMonthValue) * 100;
  const direction = lastMonthValue > previousMonthValue ? 'up' : 'down';

  return { direction, percentage: Math.abs(percentageChange) };
};

export const processTrafficSources = (
  trafficSources: Record<string, number> | undefined | null,
  maxEntries = 4,
) => {
  if (!Object.keys(trafficSources ?? {}).length) {
    return [];
  }

  const allValues = Object.entries(trafficSources ?? {})
    .map(([label, value]) => ({ label, value: Number((value * 100).toFixed(1)) }))
    .sort((a, b) => b.value - a.value);

  const topValues = allValues.slice(0, maxEntries);

  const remainingValues = allValues.slice(maxEntries);
  const otherValue = remainingValues.reduce((acc, item) => acc + item.value, 0);

  const topValuesSum = topValues.reduce((acc, item) => acc + item.value, 0);
  const total = topValuesSum + otherValue;

  const adjustmentNeeded = Number((100 - total).toFixed(1));

  const values = [...topValues];

  if (remainingValues.length > 0 || adjustmentNeeded !== 0) {
    const adjustedOtherValue = Number((otherValue + adjustmentNeeded).toFixed(1));

    if (adjustedOtherValue > 0) {
      values.push({ label: 'other', value: adjustedOtherValue });
    } else if (adjustmentNeeded !== 0 && values.length > 0) {
      values[values.length - 1]!.value = Number(
        (values[values.length - 1]!.value + adjustmentNeeded).toFixed(1),
      );
    }
  }

  const finalSum = values.reduce((acc, item) => acc + item.value, 0);

  if (finalSum !== 100 && values.length > 0) {
    const diff = Number((100 - finalSum).toFixed(1));
    values[0]!.value = Number((values[0]!.value + diff).toFixed(1));
  }

  return values;
};

export const processEngagements = (
  timeOnSite: string | number | undefined | null,
  pagesPerVisit: string | number | undefined | null,
  bounceRate: string | number | undefined | null,
) => {
  const engagements = [
    {
      label: 'Time on site',
      value: typeof timeOnSite === 'string' ? parseFloat(timeOnSite).toFixed(2) : timeOnSite,
    },
    {
      label: 'Page per visit',
      value:
        typeof pagesPerVisit === 'string' ? parseFloat(pagesPerVisit).toFixed(2) : pagesPerVisit,
    },
    {
      label: 'Bounce rate',
      value:
        typeof bounceRate === 'string' ? (parseFloat(bounceRate) * 100).toFixed(2) : bounceRate,
    },
  ] as const;

  return engagements.filter(({ value }) => typeof value === 'string') as Array<{
    label: (typeof engagements)[number]['label'];
    value: string;
  }>;
};

export const processVisitorsCountries = (
  visitorsCountries: { data?: Record<string, number> | undefined | null } | undefined | null,
) => {
  if (!Object.keys(visitorsCountries?.data ?? {}).length) {
    return [];
  }

  const values = Object.entries(visitorsCountries?.data ?? {})
    .map(([label, value]) => ({
      label,
      value: Number((value * 100).toFixed(2)),
    }))
    .sort((a, b) => b.value - a.value);

  return values;
};

export const formatVisitorsCountriesDateRange = (
  startDate: string | undefined | null,
  endDate: string | undefined | null,
): string => {
  if (!startDate || !endDate) return '';

  const startDateMMMYYYY = dayjs(startDate).format('MMM YYYY');
  const endDateMMMYYYY = dayjs(endDate).format('MMM YYYY');

  return `${startDateMMMYYYY} - ${endDateMMMYYYY}`;
};

export const formatRiskLevel = (riskLevel: string | null | undefined): string => {
  if (!riskLevel) return 'Unknown Risk';

  return `${titleCase(riskLevel)} Risk`;
};
